import create from "zustand";

export interface PhysicalReportProps {
  Hygiene: PhysicalHygieneProps;
  GeneralSigns: GeneralSignProps;
  Head: HeadProps;
  PhysicalOthers: PhysicalOtherProps;
  Lymphatic: LymphaticProps;
  Skin: SkinProps;
  Cardiovascular: CardiovascularProps;
  Respiratory: RespiratoryProps;
  Abdomen: AbdomenProps;
  Neurological: NeurologicalProps;
  Orthopedics: OrthopedicProps;
  Deficiencies_Suspected: DeficienciesSuspectedProps;
  SuggestedInvestigation: PhycsicalSuggestedInvestigation;
  PhysicalMetaData: PhysicalMetaData;
  NeedAttention: PhysicalNeedAttentionProps;
}

interface PhysicalStore extends PhysicalReportProps {
  setPhysicalReportData: (data: Record<string, any>) => void;
  reset: () => void;
}

const INITIAL_STATE: Omit<PhysicalReportProps, "NeedAttention"> = {
  Hygiene: { Hygiene_Hair: false, Hygiene_hands_and_nails: false },
  GeneralSigns: {
    General_signs_Pallor: false,
    General_signs_Cynosis: false,
    General_signs_Anemia: false,
    General_signs_Clubbing: false,
    General_signs_Edema: false,
  },
  Head: {
    Head_Abnormal_Shape: false,
    Head_Bony_Swelling: false,
    Head_Scalp_Swelling: false,
    Head_Scalp_Bruising: false,
    Head_Abnormal_Hair_Pigmentation: false,
  },
  PhysicalOthers: {
    Head_Others: "",
    Lymphatic_PLN_Others: "",
    Cardiovascular_Others: "",
    Respiratory_Others: "",
    Abdomen_Others: "",
    Neurological_Others: "",
    Orthopedics_Others: "",
    Deficiencies_Suspected_Others: "",
    Xray_Others: "",
    Other_Tests: "",
    Stool_Test_Others: "",
    Blood_Test_Others: "",
    Ultrasound_Others: "",
    General_signs_Others: "",
    Urine_Test_Others: "",
    Skin_Others: "",
    HSG_Ultrasound_Others: "",
    HSG_Xray_Others: "",
  },
  Lymphatic: {
    Lymphatic_PLN_Cervical: false,
    Lymphatic_PLN_Submandibular: false,
    Lymphatic_PLN_Pre_Auricular: false,
    Lymphatic_PLN_Submental: false,
  },
  Skin: {
    Skin_Itching: false,
    Skin_Tinea: false,
    Skin_Scabies: false,
    Skin_Impetigo: false,
    Skin_Contact_Dermatitis: false,
    Skin_Herpes: false,
    Skin_Petecheae: false,
    Skin_Purpura: false,
    Skin_Acne: false,
    Skin_Icterus: false,
    Skin_Pigmentation: false,
  },
  Cardiovascular: {
    Cardiovascular_Abnormal_heart_sounds: false,
    Cardiovascular_Murmur: false,
  },
  Respiratory: {
    Respiratory_Barky_Cough: false,
    Respiratory_Hoarse_Voice: false,
    Respiratory_Dyspnea: false,
    Respiratory_Wheezing: false,
    Respiratory_Crepts: false,
  },
  Abdomen: {
    Abdomen_Distended: false,
    Abdomen_Tense: false,
    Abdomen_Tender: false,
  },
  Neurological: {
    Neurological_Lethargy: false,
    Neurological_Slurring_of_Speech: false,
    Neurological_Abnormal_Body_Tone_increased: false,
    Neurological_Abnormal_Body_Tone_Decreased: false,
  },
  Orthopedics: {
    Orthopedics_Flat_Foot: false,
    Orthopedics_Spinal_Abnormalities_Kyphosis: false,
    Orthopedics_Spinal_Abnormalities_Scoliosis: false,
  },
  Deficiencies_Suspected: {
    Deficiencies_Suspected_Vitamins_D: false,
    Deficiencies_Suspected_Vitamins_C: false,
    Deficiencies_Suspected_Vitamins_B12: false,
    Deficiencies_Suspected_Iron: false,
    Deficiencies_Suspected_Calcium: false,
  },
  SuggestedInvestigation: {
    Physical_Doctor_Consultation: false,
    Fitness_Program: false,
    Nutrition_Program: false,
    Xray_Chest: false,
    Xray_Abdomen: false,
    Xray_Limbs: false,
    Xray_Dental: false,
    Ultrasound_Chest: false,
    Ultrasound_Abdomen: false,
    Ultrasound_Spine: false,
    CT_MRI_SCAN_Specify_Region: false,
    Blood_Test_CBC: false,
    Blood_Test_Blood_Glucose: false,
    Blood_Test_KFT: false,
    Blood_Test_LFT: false,
    Blood_Test_CA_PH: false,
    Blood_Test_Vit_D3_Levels: false,
    Blood_Test_Thyroid_Profile: false,
    Blood_Test_Lipid_Profile: false,
    Blood_Test_Vit_B12: false,
    Blood_Test_Peripheral_Smear: false,
    Stool_Test_Routine: false,
    Stool_Test_Culture: false,
    Urine_Test_Routine: false,
    Urine_Test_Culture: false,
    Urine_Test_Urine_Protein: false,
    HSG_Blood_Test_CA_PH: false,
    HSG_Blood_Test_CBC: false,
    HSG_Blood_Test_Vit_B12: false,
    HSG_Blood_Test_Vit_D3_Levels: false,
    HSG_CT_MRI_SCAN_Specify_Region: false,
    HSG_Physical_Doctor_Consultation: false,
    HSG_Urine_Test_Routine: false,
    HSG_Xray_Dental: false,
  },
  PhysicalMetaData: {
    Physical_Doctor_Hospital: "",
    Doctor_No: "",
    Physical_Doctor_Path: "",

    Physical_Doctor_Designation: "",
    Physical_Doctor_Remarks: "",
  },
};

const INITIAL_ATTENTION: PhysicalNeedAttentionProps = {
  hygiene: false,
  generalSigns: false,
  head: false,
  physicalOthers: false,
  lymphatic: false,
  skin: false,
  cardiovascular: false,
  respiratory: false,
  abdomen: false,
  neurological: false,
  orthopedics: false,
  deficiencies_Suspected: false,
  suggestedInvestigation: false,
};

export const usePhysicalReportStore = create<PhysicalStore>((set) => ({
  ...INITIAL_STATE,
  NeedAttention: { ...INITIAL_ATTENTION },

  setPhysicalReportData: (res) =>
    set((state) => {
      const data = res?.data?.data || {};

      const mapBooleans = <T extends object>(initialObj: T) => {
        const newObj = { ...initialObj };
        let hasAttention = false;

        Object.keys(newObj).forEach((key) => {
          const value = data[key] === "Y";
          newObj[key] = value;
          if (value) hasAttention = true;
        });

        return { newObj, hasAttention };
      };

      const mapStrings = (initialObj) => {
        const newObj = { ...initialObj };
        let hasAttention = false;

        Object.keys(newObj).forEach((key) => {
          const value = data[key] || "";
          newObj[key] = value;
          if (value && value !== "") hasAttention = true;
        });

        return { newObj, hasAttention };
      };

      const hygiene = mapBooleans(state.Hygiene);
      const general = mapBooleans(state.GeneralSigns);
      const head = mapBooleans(state.Head);
      const lymphatic = mapBooleans(state.Lymphatic);
      const skin = mapBooleans(state.Skin);
      const cardio = mapBooleans(state.Cardiovascular);
      const resp = mapBooleans(state.Respiratory);
      const abdomen = mapBooleans(state.Abdomen);
      const neuro = mapBooleans(state.Neurological);
      const ortho = mapBooleans(state.Orthopedics);
      const def = mapBooleans(state.Deficiencies_Suspected);
      const invest = mapBooleans(state.SuggestedInvestigation);
      const others = mapStrings(state.PhysicalOthers);

      const updatedMetaData = { ...state.PhysicalMetaData };
      Object.keys(updatedMetaData).forEach((key) => {
        updatedMetaData[key] = data[key] || "";
      });

      return {
        Hygiene: hygiene.newObj,
        GeneralSigns: general.newObj,
        Head: head.newObj,
        Lymphatic: lymphatic.newObj,
        Skin: skin.newObj,
        Cardiovascular: cardio.newObj,
        Respiratory: resp.newObj,
        Abdomen: abdomen.newObj,
        Neurological: neuro.newObj,
        Orthopedics: ortho.newObj,
        Deficiencies_Suspected: def.newObj,
        SuggestedInvestigation: invest.newObj,
        PhysicalOthers: others.newObj,
        PhysicalMetaData: updatedMetaData,
        NeedAttention: {
          hygiene: hygiene.hasAttention,
          generalSigns: general.hasAttention,
          head: head.hasAttention,
          physicalOthers: others.hasAttention,
          lymphatic: lymphatic.hasAttention,
          skin: skin.hasAttention,
          cardiovascular: cardio.hasAttention,
          respiratory: resp.hasAttention,
          abdomen: abdomen.hasAttention,
          neurological: neuro.hasAttention,
          orthopedics: ortho.hasAttention,
          deficiencies_Suspected: def.hasAttention,
          suggestedInvestigation: invest.hasAttention,
        },
      };
    }),

  reset: () =>
    set({ ...INITIAL_STATE, NeedAttention: { ...INITIAL_ATTENTION } }),
}));
